/**
 * Terminal cookie session against Grail `/api/terminal/*`.
 * Access JWT stays httpOnly — do not mirror into `window.JesterApp.authToken` (M1.1).
 */

import { oroborosFetch, oroborosJson, oroborosAuthHeaders } from './client';
import { clearAccountIdentity, isSyntheticPrincipalIdString, persistAccountIdentity } from '../../utils/walletPrincipal';
import { maybeRedirectToHyperliquidOnboarding } from '../../utils/hyperliquidOnboardingRedirect';
import { persistOnboardingExitContext } from '../../utils/onboardingExit';
import { clearStoredWebAuth, setStoredWebAuth } from '../../miniapp/api/miniappApi';
import { clearOroborosSessionSnapshot } from '../sessionCache';
import {
  clearSessionAccessToken,
  dispatchTerminalAuthResolved,
  fetchTerminalSessionRefresh,
  setTerminalSessionRefreshBlocked,
  waitForTerminalSessionRefreshIdle,
} from '../../utils/terminalSessionRefresh';
import { readHyperliquidKeyActiveFromPayload } from '../hyperliquidKeyHealth';
import { readFavoriteMarketPrefs } from './pairs';

export type OroborosSessionUser = {
  userId: string;
  /** Native Telegram chat id when this principal is (or is linked as) Telegram. */
  telegramId?: string | null;
  /** Login username from UserCredentials (Jester Account password login). */
  username?: string;
  hasPasswordLogin?: boolean;
  walletAddress?: string;
  isWalletOnly?: boolean;
  isSynthetic?: boolean;
  authMethod?: 'telegram' | 'wallet' | 'password' | 'artifice';
};

export type OroborosSessionRefresh = {
  success: boolean;
  token?: string;
  userId?: string;
  telegramId?: string | null;
  loginUsername?: string | null;
  hasPasswordLogin?: boolean;
  authMethod?: 'telegram' | 'wallet' | 'password' | 'artifice';
  requiresHyperliquidOnboarding?: boolean;
  hasHyperliquidKey?: boolean;
  /** Live extra-agent check — false when the API agent is expired, revoked, or missing. */
  hyperliquidKeyActive?: boolean;
  requiresApiKeyRefresh?: boolean;
  apiKeyStatus?: string;
  apiKeyRefreshMessage?: string;
  hyperliquidAccountMismatch?: boolean;
  /** Linked HL wallet from server (HyperliquidKeys / principals). */
  walletAddress?: string | null;
  error?: string;
  /** HTTP status from /session/refresh, or 0 on network failure. */
  status?: number;
};

function persistSessionWallet(wallet: unknown): string | undefined {
  const raw = String(wallet || '').trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(raw)) return undefined;
  const lower = raw.toLowerCase();
  try {
    const w = window as any;
    w.JesterApp = w.JesterApp || {};
    w.JesterApp.userWallet = lower;
    // Do not mirror wallet to localStorage — XSS-readable PII; use session.user.walletAddress.
  } catch {
    /* ignore */
  }
  return lower;
}

/** Clear any legacy in-memory Bearer mirror (XSS surface). */
function clearLegacyInMemoryToken() {
  try {
    const w = window as any;
    if (w.JesterApp) w.JesterApp.authToken = undefined;
  } catch {
    /* ignore */
  }
}

export function cleanOroborosTelegramLinkQueryParams(): void {
  try {
    const url = new URL(window.location.href);
    const from = url.searchParams.get('from') || '';
    if (from === 'telegram' || from.startsWith('telegram_')) url.searchParams.delete('from');
    if (url.searchParams.has('pauth')) url.searchParams.delete('pauth');
    const qs = url.searchParams.toString();
    const next = url.pathname + (qs ? `?${qs}` : '') + (url.hash || '');
    window.history.replaceState({}, '', next);
  } catch {
    /* ignore */
  }
}

/**
 * Bot-issued `?pauth=` on `/app` (Telegram menu link).
 * Exchanges opaque code → device JWT → full httpOnly session (same as Web Terminal `/app?pauth=`).
 * Bearer is used once for the device upgrade call only — never stored in JS after.
 */
export async function bootstrapOroborosTelegramLoginFromUrl(): Promise<boolean> {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const pauthCode = params.get('pauth');
    if (!pauthCode) return false;

    const ex = await oroborosJson<{ success?: boolean; token?: string }>('/api/pauth/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pauth: pauthCode }),
    });
    if (!ex.ok || !ex.data?.success || !ex.data?.token) return false;

    params.delete('pauth');
    try {
      const qs = params.toString();
      const path = window.location.pathname + (qs ? `?${qs}` : '') + (window.location.hash || '');
      window.history.replaceState({}, '', path);
    } catch {
      /* ignore */
    }

    const up = await oroborosJson<{ success?: boolean; token?: string }>('/api/terminal/session/upgrade', {
      method: 'POST',
      body: JSON.stringify({ rememberMe: true, token: ex.data.token }),
    });
    clearLegacyInMemoryToken();
    return up.ok && up.data?.success && !!up.data?.token;
  } catch {
    return false;
  }
}

/**
 * Telegram Mini App WebView: mint remember-me cookies from initData.
 * Safari does not share that cookie jar — use `openOroborosOnDesktop` to hop.
 */
export async function bootstrapOroborosTelegramWebAppSession(): Promise<boolean> {
  try {
    const initData = String((window as any)?.Telegram?.WebApp?.initData || '').trim();
    if (!initData) return false;
    const resp = await oroborosFetch('/api/terminal/auth/telegram', {
      method: 'POST',
      headers: oroborosAuthHeaders({ includeTelegramInitData: true }),
      body: JSON.stringify({ initData, rememberMe: true }),
    });
    const data = (await resp.json().catch(() => ({}))) as { success?: boolean };
    clearLegacyInMemoryToken();
    return resp.ok && !!data?.success;
  } catch {
    return false;
  }
}

export function isOroborosTelegramWebApp(): boolean {
  try {
    return Boolean(String((window as any)?.Telegram?.WebApp?.initData || '').trim());
  } catch {
    return false;
  }
}

const DESKTOP_HOP_QUERY_KEYS = [
  'nav',
  'pair',
  'timeframe',
  'strategyId',
  'page',
  'agentTab',
  'conversationId',
  'paramsHash',
] as const;

/** Issue a one-time desktop-login URL and open it outside the Mini App WebView. */
export async function openOroborosOnDesktop(): Promise<{ ok: boolean; error?: string }> {
  try {
    const initData = String((window as any)?.Telegram?.WebApp?.initData || '').trim();
    const stored = readFavoriteMarketPrefs();
    const marketPrefs =
      stored.favorites.length && stored.updatedAt === 0
        ? { ...stored, updatedAt: Date.now() }
        : stored;
    const resp = await oroborosFetch('/api/terminal/desktop-login/issue', {
      method: 'POST',
      headers: oroborosAuthHeaders({ includeTelegramInitData: true }),
      body: JSON.stringify({
        ...(initData ? { initData } : {}),
        marketPrefs,
      }),
    });
    const data = (await resp.json().catch(() => ({}))) as { success?: boolean; path?: string; error?: string };
    const path = String(data?.path || '').trim();
    if (
      !resp.ok ||
      !data?.success ||
      !(path.startsWith('/desktop-login') || path.startsWith('/oroboros/desktop-login'))
    ) {
      return { ok: false, error: data?.error || 'Could not open desktop login' };
    }
    const url = new URL(path, window.location.origin);
    if (!url.searchParams.has('from')) url.searchParams.set('from', 'telegram_desktop');
    try {
      const current = new URLSearchParams(window.location.search || '');
      for (const key of DESKTOP_HOP_QUERY_KEYS) {
        const v = String(current.get(key) || '').trim();
        if (v && !url.searchParams.has(key)) url.searchParams.set(key, v);
      }
    } catch {
      /* ignore */
    }
    const href = url.toString();
    const tg = (window as any).Telegram?.WebApp;
    if (typeof tg?.openLink === 'function') {
      tg.openLink(href);
      return { ok: true };
    }
    const win = window.open(href, '_blank', 'noopener,noreferrer');
    if (win) return { ok: true };
    window.location.assign(href);
    return { ok: true };
  } catch {
    return { ok: false, error: 'Could not open desktop login' };
  }
}

let refreshInFlight: Promise<OroborosSessionRefresh> | null = null;

function readJwtUserId(token: string): string {
  try {
    const part = String(token || '').split('.')[1] || '';
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const payload = JSON.parse(atob(b64 + pad)) as { userId?: unknown; sub?: unknown };
    if (payload?.userId != null && String(payload.userId).trim()) return String(payload.userId);
    if (payload?.sub != null && String(payload.sub).trim()) return String(payload.sub);
  } catch {
    /* ignore */
  }
  return '';
}

/**
 * Signed-out visitors used to pay for a `POST /session/refresh` → 401 on every page load (and see
 * it logged as a console error). The refresh cookie is httpOnly, so the client cannot look for it;
 * instead, a confirmed 401 sets a short-lived marker and boot-time callers skip the request while
 * it holds. Any successful refresh or login clears it, and it expires on its own.
 */
const SESSION_ABSENT_KEY = 'oro_session_absent_until';
const SESSION_ABSENT_TTL_MS = 6 * 60 * 60 * 1000;

function isSessionKnownAbsent(): boolean {
  try {
    const until = Number(localStorage.getItem(SESSION_ABSENT_KEY) || 0);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

function markSessionAbsent(): void {
  try {
    localStorage.setItem(SESSION_ABSENT_KEY, String(Date.now() + SESSION_ABSENT_TTL_MS));
  } catch {
    /* storage blocked */
  }
}

export function clearSessionAbsentMarker(): void {
  try {
    localStorage.removeItem(SESSION_ABSENT_KEY);
  } catch {
    /* storage blocked */
  }
}

async function refreshOroborosSessionOnce(): Promise<OroborosSessionRefresh> {
  // Shared same-origin refresh so overlapping Oroboros / Mini App / useAuth
  // callers cannot rotate the same refresh cookie twice.
  const { ok, data, status } = await fetchTerminalSessionRefresh();
  if (!ok || !data?.success || !data?.token) {
    clearLegacyInMemoryToken();
    if (status === 401) markSessionAbsent();
    return {
      success: false,
      error: data?.error || data?.message || 'No session',
      status: typeof status === 'number' ? status : ok ? 200 : 0,
    };
  }
  const userId = String(data.userId || '').trim() || readJwtUserId(String(data.token));
  const telegramId = String(data.telegramId || '').trim() || null;
  const loginUsername = String(data.loginUsername || '').trim() || null;
  const walletAddress = persistSessionWallet(data.walletAddress);
  clearLegacyInMemoryToken();
  clearSessionAbsentMarker();
  try {
    const { resumeOroborosNonHlSliceFetch } = await import('./portfolio');
    resumeOroborosNonHlSliceFetch();
  } catch {
    /* ignore */
  }
  try {
    setStoredWebAuth();
  } catch {
    /* ignore */
  }
  try {
    if (userId) persistAccountIdentity(userId, telegramId);
    if (loginUsername && !/^0x[a-fA-F0-9]{40}$/i.test(loginUsername)) {
      localStorage.setItem('jester_remember_username', loginUsername);
    }
  } catch {
    /* ignore */
  }
  dispatchTerminalAuthResolved();
  return {
    success: true,
    status,
    token: data.token,
    userId,
    telegramId:
      telegramId && !isSyntheticPrincipalIdString(telegramId)
        ? telegramId
        : userId && !isSyntheticPrincipalIdString(userId)
          ? userId
          : null,
    loginUsername,
    hasPasswordLogin: !!data.hasPasswordLogin || !!loginUsername,
    authMethod:
      data.authMethod === 'wallet' ||
      data.authMethod === 'password' ||
      data.authMethod === 'telegram' ||
      data.authMethod === 'artifice'
        ? data.authMethod
        : undefined,
    requiresHyperliquidOnboarding: !!data.requiresHyperliquidOnboarding,
    hasHyperliquidKey: !!data.hasHyperliquidKey,
    hyperliquidKeyActive: readHyperliquidKeyActiveFromPayload(data as Record<string, unknown>),
    requiresApiKeyRefresh: !!data.requiresApiKeyRefresh,
    apiKeyStatus: String(data.apiKeyStatus || '').trim() || undefined,
    apiKeyRefreshMessage: String(data.apiKeyRefreshMessage || '').trim() || undefined,
    hyperliquidAccountMismatch: !!data.hyperliquidAccountMismatch,
    walletAddress: walletAddress || null,
  };
}

/**
 * Rotate/refresh the httpOnly session. Concurrent callers share one in-flight
 * request so overlapping rotates cannot revoke the refresh family.
 */
export async function refreshOroborosSession(opts?: {
  /** Boot-time callers: skip the request while a recent 401 says there is no session. */
  skipIfKnownAbsent?: boolean;
}): Promise<OroborosSessionRefresh> {
  if (opts?.skipIfKnownAbsent && !refreshInFlight && isSessionKnownAbsent()) {
    return Promise.resolve({ success: false, error: 'No session', status: 401 });
  }
  if (refreshInFlight) return refreshInFlight;
  const pending = refreshOroborosSessionOnce().finally(() => {
    if (refreshInFlight === pending) refreshInFlight = null;
  });
  refreshInFlight = pending;
  return pending;
}

export async function logoutOroborosSession(): Promise<void> {
  setTerminalSessionRefreshBlocked(true);
  markSessionAbsent();
  clearSessionAccessToken();
  const pendingRefresh = refreshInFlight;
  try {
    await waitForTerminalSessionRefreshIdle();
  } catch {
    /* ignore */
  }
  if (pendingRefresh) {
    await pendingRefresh.catch(() => undefined);
  }
  try {
    await oroborosJson('/api/terminal/session/logout', { method: 'POST' });
  } catch {
    /* ignore */
  }
  clearLegacyInMemoryToken();
  clearOroborosSessionSnapshot();
  try {
    clearStoredWebAuth();
  } catch {
    /* ignore */
  }
  clearSessionAccessToken();

  // Clear all auth-related localStorage keys so next sign-in starts fresh
  try {
    localStorage.removeItem('jester_auth_token');
    clearAccountIdentity();
    localStorage.removeItem('jester_user_wallet');
    localStorage.removeItem('jester_remember_me');
    localStorage.removeItem('jester_onboarding_source');
    localStorage.removeItem('jester_onboarding_action');
    // Keep jester_remember_username for "Login as ..." UX
  } catch {
    /* ignore */
  }

  // Clear Artifice OAuth PKCE session so next OAuth starts fresh,
  // but SET the post-logout marker so next OAuth uses prompt=login
  try {
    sessionStorage.removeItem('jester_artifice_oauth');
    sessionStorage.setItem('jester_oauth_prompt', 'login');
  } catch {
    /* ignore */
  }

  // Clear Web3-Onboard wallet connection state from localStorage
  // so wallet sign-in picker reopens cleanly
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (
        key.startsWith('@web3-onboard/') ||
        key.startsWith('onboard.js') ||
        key.startsWith('WALLETCONNECT_') ||
        key.startsWith('wc@2:')
      ) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}

/**
 * Legacy full-page redirect into `/onboarding`. Prefer the in-terminal
 * `OroborosOnboardingOverlay` via `useOroborosSession().startHlOnboarding`.
 * Kept for deep links / non-React callers.
 */
export function openOroborosHyperliquidOnboarding(opts?: { action?: string }): boolean {
  const action = opts?.action || 'complete_setup';
  try {
    persistOnboardingExitContext('oroboros', action);
  } catch {
    /* ignore */
  }
  return maybeRedirectToHyperliquidOnboarding(true, {
    source: 'oroboros',
    action,
    allowMiniApp: true,
  });
}

export function sessionUserFromRefresh(res: OroborosSessionRefresh): OroborosSessionUser | null {
  if (!res.success || !res.userId) return null;
  let wallet: string | undefined =
    (res.walletAddress && /^0x[a-fA-F0-9]{40}$/i.test(res.walletAddress)
      ? res.walletAddress.toLowerCase()
      : undefined) || undefined;
  if (!wallet) {
    try {
      wallet =
        (window as any)?.JesterApp?.userWallet ||
        localStorage.getItem('jester_user_wallet') ||
        undefined;
    } catch {
      /* ignore */
    }
  }
  if (wallet && !/^0x[a-fA-F0-9]{40}$/i.test(wallet)) wallet = undefined;
  else if (wallet) wallet = wallet.toLowerCase();
  const username = String(res.loginUsername || '').trim() || undefined;
  const synthetic = isSyntheticPrincipalIdString(res.userId);
  return {
    userId: res.userId,
    telegramId: res.telegramId || undefined,
    username,
    hasPasswordLogin: !!res.hasPasswordLogin || !!username,
    authMethod: res.authMethod,
    walletAddress: wallet,
    isSynthetic: synthetic,
    isWalletOnly: synthetic,
  };
}
